import { useTranslation } from 'react-i18next';
import type { IConfidenceBand } from '@interfaces/IProductivity';
import { useCountUp } from '@hooks/useCountUp';
import { ShieldIcon } from '@utils/icons';
import { Card } from '../../shared/Card/Card';
import { CardHeader } from '../../shared/CardHeader/CardHeader';
import { ProductivityEmptyState } from '../../shared/ProductivityEmptyState/ProductivityEmptyState';
import styles from './ConfidenceMix.module.css';

interface IConfidenceMixProps {
  bands: IConfidenceBand[];
  refreshing?: boolean;
}

/** How long a bar takes to rise into place, and how far each one trails the one
 *  above it — enough of a cascade to read as one motion, not four. */
const RISE_MS = 700;
const STAGGER_MS = 80;

interface IConfidenceBandRowProps {
  band: IConfidenceBand;
  index: number; // Position in the stack — drives the stagger only.
}

/**
 * One band. The bar's width and the percentage read from the SAME animated
 * value, so the number can never disagree with the fill it labels.
 */
function ConfidenceBandRow({ band, index }: IConfidenceBandRowProps) {
  const { t } = useTranslation();
  const percent = useCountUp(band.percent, RISE_MS, index * STAGGER_MS);

  return (
    <div className={styles.band}>
      <div className={styles.bandTop}>
        <span className={styles.bandLabel}>{t(`productivity.confidenceLevel.${band.id}`)}</span>
        <span className={styles.bandPct}>{Math.round(percent)}%</span>
      </div>
      <div className={styles.track}>
        <span className={styles.fill} data-tone={band.tone} style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

export function ConfidenceMix({ bands, refreshing }: IConfidenceMixProps) {
  const { t } = useTranslation();

  return (
    <Card refreshing={refreshing}>
      <CardHeader
        size="sm"
        icon={<ShieldIcon size={16} />}
        title={t('productivity.confidence.title')}
        meta={t('productivity.confidence.scope')}
      />

      {bands.length === 0 ? (
        <ProductivityEmptyState />
      ) : (
        <>
          <div className={styles.bands}>
            {bands.map((band, index) => (
              <ConfidenceBandRow key={band.id} band={band} index={index} />
            ))}
          </div>

          <p className={styles.note}>{t('productivity.confidence.note')}</p>
        </>
      )}
    </Card>
  );
}